import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BlockEmailEditor, blocksToHtml, htmlToBlocks } from '../components/BlockEmailEditor';
import {
  ArrowLeft, Loader2, Save, Plus, Trash2, X, Check, Send, Mail, Users, Clock,
  Zap, Play, Pause, ChevronRight, ChevronDown, Shield, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { key: 'audience', label: 'Audience', icon: Users },
  { key: 'emails', label: 'Email Sequence', icon: Mail },
  { key: 'sender', label: 'Sender Info', icon: Send },
  { key: 'schedule', label: 'Schedule', icon: Clock },
  { key: 'analytics', label: 'Analytics', icon: Zap },
];

const MERGE_TAGS = [
  '{{customer_first_name}}', '{{customer_last_name}}', '{{customer_name}}',
  '{{company_name}}', '{{company_phone}}', '{{estimate_link}}',
  '{{invoice_link}}', '{{unsubscribe_url}}',
];

const FILTER_FIELDS = [
  { value: 'customer_type', label: 'Customer Type', type: 'select', options: ['residential', 'business'] },
  { value: 'customer_tag', label: 'Customer Tag', type: 'string' },
  { value: 'customer_created_date', label: 'Customer Created Date', type: 'date' },
  { value: 'last_job_date', label: 'Last Job Date', type: 'number' },
  { value: 'city', label: 'City', type: 'string' },
  { value: 'state', label: 'State', type: 'string' },
  { value: 'zip_code', label: 'ZIP Code', type: 'string' },
  { value: 'estimate_status', label: 'Estimate Status', type: 'select', options: ['draft', 'sent', 'approved', 'rejected'] },
  { value: 'estimate_age_days', label: 'Estimate Age (days)', type: 'number' },
  { value: 'estimate_total', label: 'Estimate Total', type: 'number' },
  { value: 'job_status', label: 'Job Status', type: 'select', options: ['scheduled', 'in_progress', 'paused', 'completed', 'cancelled'] },
  { value: 'job_type', label: 'Job Type', type: 'string' },
  { value: 'job_count', label: 'Job Count', type: 'number' },
  { value: 'invoice_status', label: 'Invoice Status', type: 'select', options: ['draft', 'sent', 'paid', 'overdue', 'void'] },
  { value: 'total_customer_spend', label: 'Total Customer Spend ($)', type: 'number' },
];

const OPERATORS = {
  string: ['equals', 'not_equals', 'contains'],
  number: ['gt', 'lt', 'gte', 'lte', 'equals'],
  date: ['after', 'before'],
  select: ['equals', 'not_equals'],
};

const EXIT_CONDITIONS = [
  { value: 'estimate_accepted', label: 'Estimate Accepted' },
  { value: 'job_created', label: 'Job Created' },
  { value: 'invoice_paid', label: 'Invoice Paid' },
  { value: 'customer_tag_added', label: 'Customer Tag Added' },
];

export default function CampaignBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const [audiences, setAudiences] = useState([]);
  const [showAudienceBuilder, setShowAudienceBuilder] = useState(false);

  // Audience builder state
  const [newAudience, setNewAudience] = useState({ name: '', description: '', filters: [] });
  const [audiencePreview, setAudiencePreview] = useState(null);

  // Email editor state
  const [editingEmail, setEditingEmail] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const [campRes, audRes] = await Promise.all([
        campaignsAPI.get(id),
        campaignsAPI.listAudiences(),
      ]);
      setCampaign(campRes.data);
      setAudiences(audRes.data);
    } catch { toast.error('Failed to load campaign'); navigate('/marketing/campaigns'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { fetch(); }, [fetch]);

  const save = async (updates) => {
    setSaving(true);
    try {
      await campaignsAPI.update(id, updates);
      await fetch();
      toast.success('Saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleStatus = async () => {
    if (!campaign) return;
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    await save({ status: newStatus });
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin" /></div></Layout>;
  if (!campaign) return null;

  const StepContent = [AudienceStep, EmailsStep, SenderStep, ScheduleStep, AnalyticsStep][step];

  return (
    <Layout>
    <div className="space-y-6" data-testid="campaign-builder">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/campaigns')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <Input
            value={campaign.name}
            onChange={e => setCampaign({ ...campaign, name: e.target.value })}
            onBlur={() => save({ name: campaign.name })}
            className="text-lg font-bold border-none shadow-none px-0 h-auto focus-visible:ring-0"
            data-testid="campaign-name-input"
          />
          <div className="flex items-center gap-2 mt-1">
            <Badge className={`text-[10px] ${campaign.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
              {campaign.status}
            </Badge>
            <span className="text-xs text-muted-foreground capitalize">{campaign.campaign_type?.replace('_', '-')}</span>
          </div>
        </div>
        <Button
          variant={campaign.status === 'active' ? 'outline' : 'default'}
          size="sm"
          onClick={toggleStatus}
          disabled={saving || !campaign.emails?.length}
          data-testid="toggle-status-btn"
        >
          {campaign.status === 'active' ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pause</> : <><Play className="w-3.5 h-3.5 mr-1" /> Activate</>}
        </Button>
      </div>

      {/* Step Navigation */}
      <div className="flex items-center gap-1 border-b pb-3">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i === step;
          return (
            <button
              key={s.key}
              onClick={() => setStep(i)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
              data-testid={`step-${s.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Step Content */}
      <StepContent
        campaign={campaign}
        setCampaign={setCampaign}
        save={save}
        saving={saving}
        audiences={audiences}
        setAudiences={setAudiences}
        showAudienceBuilder={showAudienceBuilder}
        setShowAudienceBuilder={setShowAudienceBuilder}
        newAudience={newAudience}
        setNewAudience={setNewAudience}
        audiencePreview={audiencePreview}
        setAudiencePreview={setAudiencePreview}
        editingEmail={editingEmail}
        setEditingEmail={setEditingEmail}
        fetch={fetch}
      />

      {/* Step Navigation Buttons */}
      <div className="flex justify-between pt-4 border-t">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep(s => s - 1)}>Back</Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={() => setStep(s => s + 1)}>Next <ChevronRight className="w-3.5 h-3.5 ml-1" /></Button>
        ) : (
          <Button onClick={toggleStatus} disabled={saving || !campaign.emails?.length} data-testid="final-activate-btn">
            {campaign.status === 'active' ? 'Pause Campaign' : 'Activate Campaign'}
          </Button>
        )}
      </div>
    </div>
    </Layout>
  );
}


/* ============ Step 1: Audience ============ */
function AudienceStep({ campaign, save, audiences, setAudiences, showAudienceBuilder, setShowAudienceBuilder, newAudience, setNewAudience, audiencePreview, setAudiencePreview }) {
  const createAudience = async () => {
    if (!newAudience.name.trim()) { toast.error('Audience name required'); return; }
    try {
      const res = await campaignsAPI.createAudience(newAudience);
      toast.success('Audience created');
      await save({ audience_id: res.data.id });
      const audRes = await campaignsAPI.listAudiences();
      setAudiences(audRes.data);
      setShowAudienceBuilder(false);
      setNewAudience({ name: '', description: '', filters: [] });
    } catch { toast.error('Failed to create audience'); }
  };

  const previewAudience = async (audId) => {
    try {
      const res = await campaignsAPI.previewAudience(audId);
      setAudiencePreview(res.data);
    } catch { toast.error('Preview failed'); }
  };

  const addFilter = () => {
    setNewAudience(a => ({ ...a, filters: [...a.filters, { field: 'customer_tag', operator: 'equals', value: '', logic_group: 0 }] }));
  };

  const updateFilter = (idx, updates) => {
    setNewAudience(a => ({ ...a, filters: a.filters.map((f, i) => i === idx ? { ...f, ...updates } : f) }));
  };

  const removeFilter = (idx) => {
    setNewAudience(a => ({ ...a, filters: a.filters.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Select Audience</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {audiences.length > 0 && (
            <div className="space-y-2">
              {audiences.map(a => (
                <div
                  key={a.id}
                  className={`flex items-center justify-between px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${campaign.audience_id === a.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                  onClick={() => { save({ audience_id: a.id }); previewAudience(a.id); }}
                  data-testid={`audience-option-${a.id}`}
                >
                  <div>
                    <p className="text-sm font-medium">{a.name}</p>
                    {a.description && <p className="text-xs text-muted-foreground">{a.description}</p>}
                    <p className="text-[10px] text-muted-foreground mt-1">{a.filters?.length || 0} filter(s)</p>
                  </div>
                  {campaign.audience_id === a.id && <Check className="w-4 h-4 text-primary" />}
                </div>
              ))}
            </div>
          )}

          {audiencePreview && (
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
              <p className="text-xs font-medium text-blue-900">Audience Preview: {audiencePreview.count} customer(s)</p>
              {audiencePreview.sample_names?.length > 0 && (
                <p className="text-[10px] text-blue-700 mt-1">Sample: {audiencePreview.sample_names.join(', ')}</p>
              )}
            </div>
          )}

          {!showAudienceBuilder ? (
            <Button variant="outline" size="sm" onClick={() => setShowAudienceBuilder(true)} data-testid="new-audience-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Create New Audience
            </Button>
          ) : (
            <Card className="border-dashed">
              <CardContent className="pt-4 space-y-3">
                <Input value={newAudience.name} onChange={e => setNewAudience(a => ({ ...a, name: e.target.value }))} placeholder="Audience name" className="h-8 text-xs" data-testid="new-audience-name" />
                <Input value={newAudience.description} onChange={e => setNewAudience(a => ({ ...a, description: e.target.value }))} placeholder="Description (optional)" className="h-8 text-xs" />

                {/* Filters */}
                <div className="space-y-2">
                  <p className="text-xs font-medium">Filters</p>
                  {newAudience.filters.map((f, idx) => {
                    const fieldDef = FILTER_FIELDS.find(ff => ff.value === f.field) || {};
                    const ops = OPERATORS[fieldDef.type || 'string'] || OPERATORS.string;
                    return (
                      <div key={idx} className="flex gap-2 items-center" data-testid={`filter-row-${idx}`}>
                        <Select value={f.field} onValueChange={v => updateFilter(idx, { field: v, operator: 'equals', value: '' })}>
                          <SelectTrigger className="h-7 text-xs w-40"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FILTER_FIELDS.map(ff => <SelectItem key={ff.value} value={ff.value}>{ff.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Select value={f.operator} onValueChange={v => updateFilter(idx, { operator: v })}>
                          <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ops.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {fieldDef.type === 'select' ? (
                          <Select value={f.value} onValueChange={v => updateFilter(idx, { value: v })}>
                            <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>
                              {(fieldDef.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input value={f.value} onChange={e => updateFilter(idx, { value: e.target.value })} placeholder="Value" className="h-7 text-xs flex-1" />
                        )}
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeFilter(idx)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    );
                  })}
                  <Button variant="ghost" size="sm" onClick={addFilter} className="text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Add Filter
                  </Button>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={createAudience} data-testid="save-audience-btn">Create Audience</Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowAudienceBuilder(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


/* ============ Step 2: Email Sequence ============ */
function EmailsStep({ campaign, fetch, editingEmail, setEditingEmail }) {
  const emails = campaign.emails || [];
  const [form, setForm] = useState({ subject: '', preview_text: '', html_content: '', delay_days: 0, send_time: '09:00' });

  const startAdd = () => {
    setForm({ subject: '', preview_text: '', html_content: '', delay_days: emails.length > 0 ? 3 : 0, send_time: '09:00' });
    setEditingEmail('new');
  };

  const startEdit = (email) => {
    setForm({ subject: email.subject, preview_text: email.preview_text || '', html_content: email.html_content, delay_days: email.delay_days, send_time: email.send_time || '09:00' });
    setEditingEmail(email.id);
  };

  const saveEmail = async () => {
    if (!form.subject.trim()) { toast.error('Subject required'); return; }
    try {
      if (editingEmail === 'new') {
        await campaignsAPI.addEmail(campaign.id, { ...form, email_order: emails.length });
      } else {
        await campaignsAPI.updateEmail(campaign.id, editingEmail, form);
      }
      setEditingEmail(null);
      await fetch();
      toast.success('Email saved');
    } catch { toast.error('Failed to save email'); }
  };

  const deleteEmail = async (emailId) => {
    try {
      await campaignsAPI.deleteEmail(campaign.id, emailId);
      await fetch();
      toast.success('Email removed');
    } catch { toast.error('Failed to remove email'); }
  };

  const insertTag = (tag) => {
    setForm(f => ({ ...f, html_content: f.html_content + tag }));
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Email Sequence</CardTitle>
            <Button variant="outline" size="sm" onClick={startAdd} data-testid="add-email-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Email
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {emails.length === 0 && !editingEmail && (
            <div className="text-center py-8">
              <Mail className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-xs text-muted-foreground">No emails in this sequence yet</p>
            </div>
          )}

          {emails.map((email, idx) => (
            editingEmail === email.id ? (
              <EmailEditor key={email.id} form={form} setForm={setForm} onSave={saveEmail} onCancel={() => setEditingEmail(null)} insertTag={insertTag} />
            ) : (
              <div key={email.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border hover:bg-muted/30 group" data-testid={`email-row-${email.id}`}>
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-primary">{idx + 1}</span>
                </div>
                {idx > 0 && (
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full shrink-0">
                    <Clock className="w-2.5 h-2.5" /> +{email.delay_days}d
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{email.subject || '(no subject)'}</p>
                  {email.preview_text && <p className="text-xs text-muted-foreground truncate">{email.preview_text}</p>}
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => startEdit(email)} data-testid={`edit-email-${email.id}`}>Edit</Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => deleteEmail(email.id)}>
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                </div>
              </div>
            )
          ))}

          {editingEmail === 'new' && (
            <EmailEditor form={form} setForm={setForm} onSave={saveEmail} onCancel={() => setEditingEmail(null)} insertTag={insertTag} />
          )}
        </CardContent>
      </Card>

      {/* Exit Conditions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Shield className="w-4 h-4" /> Exit Conditions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">Stop the sequence if any of these events occur:</p>
          {(campaign.exit_conditions || []).map(ec => (
            <div key={ec.id} className="flex items-center justify-between px-3 py-2 rounded border text-xs">
              <span>{EXIT_CONDITIONS.find(e => e.value === ec.condition_event)?.label || ec.condition_event}</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={async () => {
                await campaignsAPI.deleteExitCondition(campaign.id, ec.id);
                await fetch();
              }}><X className="w-3 h-3" /></Button>
            </div>
          ))}
          <Select onValueChange={async (val) => {
            await campaignsAPI.addExitCondition(campaign.id, { condition_type: 'event', condition_event: val });
            await fetch();
          }}>
            <SelectTrigger className="h-8 text-xs w-56"><SelectValue placeholder="Add exit condition..." /></SelectTrigger>
            <SelectContent>
              {EXIT_CONDITIONS.filter(ec => !(campaign.exit_conditions || []).some(e => e.condition_event === ec.value))
                .map(ec => <SelectItem key={ec.value} value={ec.value}>{ec.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
    </div>
  );
}


/* ============ Email Editor ============ */
function EmailEditor({ form, setForm, onSave, onCancel, insertTag }) {
  const [blocks, setBlocks] = useState(() => {
    // Parse existing HTML into blocks if available
    if (form.html_content) {
      const parsed = htmlToBlocks(form.html_content);
      if (parsed.length > 0) return parsed;
    }
    return [];
  });

  const handleBlocksChange = (newBlocks) => {
    setBlocks(newBlocks);
    const html = blocksToHtml(newBlocks);
    setForm(f => ({ ...f, html_content: html }));
  };

  return (
    <Card className="border-primary/30 bg-primary/5" data-testid="email-editor">
      <CardContent className="pt-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-medium text-muted-foreground uppercase">Subject</label>
            <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Email subject line..." className="h-8 text-xs mt-1" data-testid="email-subject" />
          </div>
          <div>
            <label className="text-[10px] font-medium text-muted-foreground uppercase">Preview Text</label>
            <Input value={form.preview_text} onChange={e => setForm(f => ({ ...f, preview_text: e.target.value }))} placeholder="Preview text..." className="h-8 text-xs mt-1" data-testid="email-preview" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-medium text-muted-foreground uppercase">Delay (days after previous)</label>
            <Input type="number" min={0} value={form.delay_days} onChange={e => setForm(f => ({ ...f, delay_days: parseInt(e.target.value) || 0 }))} className="h-8 text-xs mt-1" data-testid="email-delay" />
          </div>
          <div>
            <label className="text-[10px] font-medium text-muted-foreground uppercase">Send Time</label>
            <Input type="time" value={form.send_time} onChange={e => setForm(f => ({ ...f, send_time: e.target.value }))} className="h-8 text-xs mt-1" data-testid="email-send-time" />
          </div>
        </div>
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase mb-2 block">Email Content</label>
          <BlockEmailEditor
            blocks={blocks}
            onChange={handleBlocksChange}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm" onClick={onSave} data-testid="save-email-btn"><Save className="w-3 h-3 mr-1" /> Save Email</Button>
        </div>
      </CardContent>
    </Card>
  );
}


/* ============ Step 3: Sender Info ============ */
function SenderStep({ campaign, save, saving }) {
  const navigate = useNavigate();
  const [fromEmail, setFromEmail] = useState(campaign.from_email || '');
  const [fromName, setFromName] = useState(campaign.from_name || '');

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Sender Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-xs font-medium">From Name</label>
          <Input value={fromName} onChange={e => setFromName(e.target.value)} placeholder="Your Company Name" className="h-8 text-xs mt-1" data-testid="from-name-input" />
        </div>
        <div>
          <label className="text-xs font-medium">From Email</label>
          <Input value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder="noreply@yourcompany.com" className="h-8 text-xs mt-1" data-testid="from-email-input" />
        </div>
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-amber-900">Email Provider</p>
              <p className="text-[10px] text-amber-700 mt-1">
                Campaign emails are sent through your configured email provider.
              </p>
              <button
                type="button"
                onClick={() => navigate('/company-settings?tab=email')}
                className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 hover:text-amber-950 underline underline-offset-2 transition-colors"
                data-testid="configure-email-settings-link"
              >
                <Mail className="w-3 h-3" />
                Configure Email Settings
              </button>
            </div>
          </div>
        </div>
        <Button size="sm" onClick={() => save({ from_email: fromEmail, from_name: fromName })} disabled={saving} data-testid="save-sender-btn">
          <Save className="w-3 h-3 mr-1" /> Save Sender Info
        </Button>
      </CardContent>
    </Card>
  );
}


/* ============ Step 4: Schedule ============ */
function ScheduleStep({ campaign, save, saving }) {
  const [type, setType] = useState(campaign.campaign_type || 'one_time');
  const [triggerType, setTriggerType] = useState(campaign.trigger_type || 'manual');
  const [triggerEvent, setTriggerEvent] = useState(campaign.trigger_event || '');
  const [scheduledAt, setScheduledAt] = useState(campaign.scheduled_at ? campaign.scheduled_at.slice(0, 16) : '');

  const handleSave = () => {
    save({
      campaign_type: type,
      trigger_type: triggerType,
      trigger_event: triggerEvent || null,
      scheduled_at: scheduledAt || null,
    });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Schedule & Trigger</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-xs font-medium">Campaign Type</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-8 text-xs mt-1 w-48" data-testid="campaign-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="one_time">One-time</SelectItem>
              <SelectItem value="automated">Automated</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium">Trigger Type</label>
          <Select value={triggerType} onValueChange={setTriggerType}>
            <SelectTrigger className="h-8 text-xs mt-1 w-48" data-testid="trigger-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="schedule">Scheduled</SelectItem>
              <SelectItem value="event">Event-based</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {triggerType === 'schedule' && (
          <div>
            <label className="text-xs font-medium">Scheduled Date & Time</label>
            <Input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} className="h-8 text-xs mt-1 w-64" data-testid="scheduled-at-input" />
          </div>
        )}
        {triggerType === 'event' && (
          <div>
            <label className="text-xs font-medium">Trigger Event</label>
            <Select value={triggerEvent} onValueChange={setTriggerEvent}>
              <SelectTrigger className="h-8 text-xs mt-1 w-64" data-testid="trigger-event-select"><SelectValue placeholder="Select event..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="estimate_created">Estimate Created</SelectItem>
                <SelectItem value="estimate_sent">Estimate Sent</SelectItem>
                <SelectItem value="job_completed">Job Completed</SelectItem>
                <SelectItem value="invoice_sent">Invoice Sent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-schedule-btn">
          <Save className="w-3 h-3 mr-1" /> Save Schedule
        </Button>
      </CardContent>
    </Card>
  );
}


/* ============ Analytics Step ============ */
function AnalyticsStep({ campaign, refreshCampaign }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await campaignsAPI.getAnalytics(campaign.id);
        setAnalytics(res.data);
      } catch { toast.error('Failed to load analytics'); }
      finally { setLoading(false); }
    })();
  }, [campaign.id]);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!analytics) return <p className="text-xs text-muted-foreground text-center py-8">No analytics data available yet.</p>;

  const { overall, per_email, timeline } = analytics;

  const statCards = [
    { label: 'Sent', value: overall.sent, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Delivered', value: overall.delivered, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Opened', value: `${overall.unique_opens} (${overall.open_rate}%)`, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Clicked', value: `${overall.unique_clicks} (${overall.click_rate}%)`, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Unsubscribed', value: `${overall.unsubscribed} (${overall.unsubscribe_rate}%)`, color: 'text-red-600', bg: 'bg-red-50' },
  ];

  return (
    <div className="space-y-4" data-testid="analytics-step">
      {/* KPI Cards */}
      <div className="grid grid-cols-5 gap-2">
        {statCards.map(sc => (
          <Card key={sc.label} className={`p-3 ${sc.bg}`}>
            <p className={`text-lg font-bold ${sc.color}`}>{sc.value}</p>
            <p className="text-[10px] font-medium text-muted-foreground uppercase">{sc.label}</p>
          </Card>
        ))}
      </div>

      {/* Per-email breakdown */}
      {per_email?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Per-Email Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left py-1 font-medium">#</th>
                  <th className="text-left py-1 font-medium">Subject</th>
                  <th className="text-right py-1 font-medium">Sent</th>
                  <th className="text-right py-1 font-medium">Opens</th>
                  <th className="text-right py-1 font-medium">Open %</th>
                  <th className="text-right py-1 font-medium">Clicks</th>
                  <th className="text-right py-1 font-medium">Click %</th>
                </tr>
              </thead>
              <tbody>
                {per_email.map(e => (
                  <tr key={e.email_id} className="border-b last:border-0">
                    <td className="py-1.5">{e.email_order}</td>
                    <td className="py-1.5 font-medium">{e.subject || '(no subject)'}</td>
                    <td className="py-1.5 text-right">{e.sent}</td>
                    <td className="py-1.5 text-right">{e.opened}</td>
                    <td className="py-1.5 text-right">{e.open_rate}%</td>
                    <td className="py-1.5 text-right">{e.clicked}</td>
                    <td className="py-1.5 text-right">{e.click_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Activity Timeline */}
      {timeline?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5 max-h-60 overflow-y-auto">
              {timeline.map(ev => (
                <div key={ev.id} className="flex items-center gap-2 text-xs py-1 border-b last:border-0">
                  <EventBadge type={ev.event_type} />
                  <span className="font-medium">{ev.customer_name || 'Unknown'}</span>
                  <span className="text-muted-foreground flex-1 truncate">
                    {ev.event_type === 'clicked' && ev.metadata ? (() => {
                      try { return JSON.parse(ev.metadata)?.url || ''; } catch { return ''; }
                    })() : ''}
                  </span>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {ev.created_at ? new Date(ev.created_at).toLocaleString() : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {overall.sent === 0 && (
        <div className="text-center py-8">
          <Mail className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-xs text-muted-foreground">No emails have been sent yet. Start your campaign to see analytics.</p>
        </div>
      )}
    </div>
  );
}

function EventBadge({ type }) {
  const styles = {
    sent: 'bg-blue-100 text-blue-700',
    opened: 'bg-amber-100 text-amber-700',
    clicked: 'bg-purple-100 text-purple-700',
    unsubscribed: 'bg-red-100 text-red-700',
    send_failed: 'bg-gray-100 text-gray-700',
  };
  return (
    <Badge variant="secondary" className={`text-[9px] ${styles[type] || ''}`} data-testid={`event-badge-${type}`}>
      {type}
    </Badge>
  );
}
